import React from 'react'
import { Link } from 'gatsby'
import Centered from 'shared/ui/Centered'
import Tiles from 'shared/ui/Tiles'
import Heading from 'shared/ui/Heading'
import Icon from 'shared/ui/Icon'
import ContentTile from 'shared/ui/ContentTile'
import { IconList, IconListItem, Icon as IconListIcon } from 'shared/ui/IconList'
import { Hero, HeroPrimaryContent } from '../ui/Hero'
import Card, { CardFullBleedImageHeader } from '../ui/Card'
import AboutUs from '../ui/AboutUs'
import { NewsletterSubscribeSection } from '../ui/NewsletterSubscribeForm'
import { SlantBackground, SlantBackgroundImage } from '../ui/SlantBackground'
import GridSlants from '../ui/GridSlants'
import { IconContent, IconContentText } from 'shared/ui/IconContent'
import { TweetGrid, TweetGridCell } from '../ui/TweetGrid'
import ClampTextLines from '../ui/ClampTextLines'
import { Tweet, TweetContent } from '../ui/Tweet'
import CourseList from '../ui/CourseList'
import './index.scss'

export default function HomePage() {
  return (
    <div className="home-page spacing-giant">
      <div>
        <Hero>
          <HeroPrimaryContent>
            <div className="text-large">Professional</div>
            <Heading className="text-color-blue">
              <span className="text-giant">React Trainings</span>
            </Heading>
            <IconList spacing="small" iconColor="brightBlue" iconSize={1.3}>
              <IconListItem>
                <IconListIcon />
                <span className="text-small">Big or small teams</span>
              </IconListItem>
              <IconListItem>
                <IconListIcon />
                <span className="text-small">Core fundamentals and advanced</span>
              </IconListItem>
              <IconListItem>
                <IconListIcon />
                <span className="text-small">Private and public workshops</span>
              </IconListItem>
              <IconListItem>
                <IconListIcon />
                <span className="text-small">Remote or on-site (pending with COVID-19)</span>
              </IconListItem>
            </IconList>
            <Link to="/corporate-training" className="button max-950-none">
              Plan a Training
            </Link>
          </HeroPrimaryContent>
          <HeroTweetGrid />
        </Hero>
        <GridSlants cells={1} className="primary-hero-grid-slants" />
      </div>

      <SlantBackground verticalOffset={-6} shallowSlant>
        <SlantBackgroundImage src="/images/react-gray-two.svg" />
      </SlantBackground>

      <Centered>
        <div className="spacing-giant">
          <section className="offset-slant-background-image-left">
            <ul className="list-reset spacing">
              <IconContent as="li" className="spacing-small">
                <IconContentText as={Heading} size={3}>
                  <Icon name="arrow-right" size={1.5} color="brightBlue" />
                  <strong>In-person and Remote Trainings</strong>
                </IconContentText>
                <p>
                  Attendees will spend two days writing various parts of a real web application.
                </p>
              </IconContent>
              <IconContent as="li" className="spacing-small">
                <IconContentText as={Heading} size={3}>
                  <Icon name="arrow-right" size={1.5} color="brightBlue" />
                  <strong>JavaScript & Node Training</strong>
                </IconContentText>
                <p>
                  If your team needs to get caught up on JavaScript, we can help with some free
                  material before the workshop occurs and we also make time during the workshop to
                  discuss any JavaScript questions as needed.
                </p>
              </IconContent>
              <IconContent as="li" className="spacing-small">
                <IconContentText as={Heading} size={3}>
                  <Icon name="arrow-right" size={1.5} color="brightBlue" />
                  <strong>Post-Workshop Online Resources</strong>
                </IconContentText>
                <p>
                  After our workshops, you'll be able to continue learning and have great reference
                  material with our <Link to="/learn">online resources</Link>
                </p>
                <br />
                <footer className="learn-more-footer spacing">
                  <div className="spacing">
                    <Link to="/workshops" className="button button-light">
                      More About Workshops
                    </Link>
                    <Link to="/benefits-of-professional-training" className="button button-light">
                      Why Get Training?
                    </Link>
                  </div>
                  <p className="text-small">
                    <Link to="/what-is-react">What is React</Link> and{' '}
                    <Link to="/what-is-react">how can I explain it to my boss?</Link>
                  </p>
                </footer>
              </IconContent>
            </ul>
          </section>

          <Card>
            <CardFullBleedImageHeader src="/images/workshop-audience-one.jpg" />
            <div className="brands text-small">
              <Link
                to="/brands"
                className="brands-img-link"
                aria-label="See the brands that trust us"
              >
                <img src="/images/brands/apple.svg" title="Apple" alt="Apple" loading="lazy" />
                <img src="/images/brands/google.svg" title="Google" alt="Google" loading="lazy" />
                <img src="/images/brands/adobe.svg" title="Adobe" alt="Adobe" loading="lazy" />
                <img src="/images/brands/hp.svg" title="HP" alt="HP" loading="lazy" />
                <img
                  src="/images/brands/netflix.svg"
                  title="Netflix"
                  alt="Netflix"
                  loading="lazy"
                />
                <img
                  src="/images/brands/microsoft.svg"
                  title="Microsoft"
                  alt="Microsoft"
                  loading="lazy"
                />
                <img
                  src="/images/brands/starbucks.svg"
                  title="Starbucks"
                  alt="Starbucks"
                  loading="lazy"
                />
              </Link>
            </div>
          </Card>
        </div>
      </Centered>

      <SlantBackground slantRight verticalOffset={-10} />

      <Centered className="spacing-giant">
        <Tiles minSize={19} gutter={2}>
          <ContentTile title="React Workshop" iconName="react-color" iconColor="brightBlue">
            <p className="text-small">
              React is known for making UI components, but in a professional React app a component
              is used for so much more. We'll teach your team how to use React's components for
              scalable, accessible and reusable abstractions. Also,{' '}
              <Link to="/what-are-react-hooks">what are React Hooks?</Link>
            </p>
            <Link
              to="/corporate-training#react-workshop"
              className="button button-light"
              aria-label="Learn More about our React workshops"
            >
              Read More
            </Link>
          </ContentTile>
          <ContentTile title="JavaScript & Node Workshop" iconName="javascript-color">
            <p className="text-small">
              We offer training on JavaScript and Node. Even if your team doesn't use Node for
              servers, knowing Node is an important part of the build and tooling process for
              front-end applications, and it's crucial when running React in development mode.
            </p>
            <Link
              to="/corporate-training#javascript-workshop"
              className="button button-light"
              aria-label="Learn More about our JavaScript and Node workshops"
            >
              Read More
            </Link>
          </ContentTile>
        </Tiles>

        <div className="spacing-large">
          <hr />
          <Heading as="h2" size={3} className="align-center">
            We Also Have Online Courses
          </Heading>
          <CourseList />
          <hr />
          <AboutUs />
          <hr />
          <NewsletterSubscribeSection />
        </div>
      </Centered>
    </div>
  )
}

function HeroTweetGrid() {
  return (
    <TweetGrid>
      <TweetGridCell col={7} row={3} scale={0.8}>
        <Tweet handle="JosephGilgen" tweetId="1129561886234570752">
          <ClampTextLines lines={3}>
            <TweetContent>
              I used to think I loved React, but after 2 days training with @ReactTraining I am
              blown away at how awesome React truly is. Makes me wonder how I even liked it before.
              Thanks to @mjackson and @ryanflorence for the great workshop.
            </TweetContent>
          </ClampTextLines>
        </Tweet>
      </TweetGridCell>
      <TweetGridCell col={18} row={3}>
        <Tweet handle="nigam02" tweetId="1124429190575677440">
          <ClampTextLines lines={3}>
            <TweetContent>
              @ReactTraining Thank you for a very informative training on React Hooks, it was good
              on so many points. Your training was informative and interactive which gave me more
              confidence to use React Hooks in my future applications.
            </TweetContent>
          </ClampTextLines>
        </Tweet>
      </TweetGridCell>

      {/* they asked us to take it down
      
      <TweetGridCell col={5} row={7} scale={0.8}>
        <Tweet handle="neb_b" tweetId="1124432562657603585">
          <ClampTextLines lines={3}>
            <TweetContent>
              If you are thinking about attending a @ReactTraining workshop, stop thinking and just
              do it. So many light bulbs have been going off the last two days with hooks and
              context. I had a basic understanding before the workshop but now I feel like I can
              build anything! #reactjs
            </TweetContent>
          </ClampTextLines>
        </Tweet>
      </TweetGridCell> */}

      <TweetGridCell col={8} row={8} scale={0.7}>
        <Tweet handle="sethdavis512" tweetId="1128824042297995265">
          <ClampTextLines lines={3}>
            <TweetContent>
              Thanks @mjackson & @ryanflorence for an AWESOME @ReactTraining! I'm already cooking up
              some ideas for some new components{' '}
              <span role="img" aria-label="smile face with glasses">
                😎
              </span>
            </TweetContent>
          </ClampTextLines>
        </Tweet>
      </TweetGridCell>
      <TweetGridCell col={12} row={5}>
        <Tweet handle="holtbt" tweetId="605791121071968256">
          <ClampTextLines lines={3}>
            <TweetContent>
              Sitting in @ReactTraining listening to @ryanflorence. Wish I'd learned React this way.
              Would have saved me a bunch of time.
            </TweetContent>
          </ClampTextLines>
        </Tweet>
      </TweetGridCell>
      <TweetGridCell col={23} row={6}>
        <Tweet handle="erikiva" tweetId="1123695004160069634">
          <ClampTextLines lines={3}>
            <TweetContent>
              @ReactTraining is hands down the most fun I’ve had whilst learning a shit ton of
              stuff. Thank you @ryanflorence, @mjackson and @WayfairTech for organizing. Loved how
              much cool stuff the material covers
            </TweetContent>
          </ClampTextLines>
        </Tweet>
      </TweetGridCell>
      <TweetGridCell col={18} row={9}>
        <Tweet handle="renee_lung" tweetId="1243675836860862465">
          <ClampTextLines lines={3}>
            <TweetContent>
              Thanks @bradwestfall and @ReactTraining for a siiiiickk workshop! I think maybe the
              best part is like how much Brad just looooooooves React hahaha
            </TweetContent>
          </ClampTextLines>
        </Tweet>
      </TweetGridCell>
    </TweetGrid>
  )
}
